// ThemePicker.jsx
// Compact dropdown that swaps the dashboard-wide colour palette. Six curated
// presets + a "reset to default". Persists on Dashboard.themePalette so
// every panel picks up the change on next render.

import React, { useState, useRef, useEffect } from 'react';
import { Palette, Check } from 'lucide-react';
import { COLORS as DEFAULT_COLORS } from './constants';

// Presets — each is a 6-colour palette. Names shown in the picker.
export const THEME_PRESETS = [
  { name: 'Miralys', palette: DEFAULT_COLORS.slice(0, 6) },
  { name: 'Ocean',    palette: ['#0ea5e9', '#06b6d4', '#14b8a6', '#22d3ee', '#3b82f6', '#6366f1'] },
  { name: 'Sunset',   palette: ['#f97316', '#f59e0b', '#ef4444', '#ec4899', '#a855f7', '#f43f5e'] },
  { name: 'Forest',   palette: ['#10b981', '#84cc16', '#22c55e', '#059669', '#65a30d', '#16a34a'] },
  { name: 'Mono violet', palette: ['#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd', '#6d28d9', '#5b21b6'] },
  { name: 'Corporate', palette: ['#1f2937', '#374151', '#4b5563', '#6b7280', '#9ca3af', '#d1d5db'] },
  { name: 'High contrast', palette: ['#facc15', '#f97316', '#ef4444', '#22c55e', '#3b82f6', '#a855f7'] }
];

export function resolvePalette(themePalette) {
  return (Array.isArray(themePalette) && themePalette.length >= 3)
    ? themePalette
    : DEFAULT_COLORS;
}

export default function ThemePicker({
  themePalette = [], themeName = '',
  onChange, darkMode = true, readOnly = false
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const T = darkMode ? {
    text: '#e5e7eb', mute: '#94a3b8', accent: '#8b5cf6',
    bg: '#0f172a', border: '#1f2937', hover: 'rgba(139,92,246,0.14)'
  } : {
    text: '#0f172a', mute: '#64748b', accent: '#7c3aed',
    bg: '#ffffff', border: '#e2e8f0', hover: 'rgba(124,58,237,0.10)'
  };

  const currentName = themeName || 'Default';

  if (readOnly) {
    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 11, color: T.mute
      }}>
        <Palette size={11} /> {currentName}
      </div>
    );
  }

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '5px 10px', borderRadius: 6,
          background: 'transparent', border: `1px solid ${T.border}`,
          color: T.text, fontSize: 11, fontWeight: 600, cursor: 'pointer'
        }}
        title="Change dashboard theme"
      >
        <Palette size={12} color={T.accent} />
        <span>{currentName}</span>
        {themePalette.length >= 3 && (
          <div style={{ display: 'inline-flex', gap: 1, marginLeft: 2 }}>
            {themePalette.slice(0, 3).map((c, i) => (
              <div key={i} style={{ width: 8, height: 8, borderRadius: 2, background: c }} />
            ))}
          </div>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0,
          minWidth: 200, background: T.bg,
          border: `1px solid ${T.border}`, borderRadius: 8,
          boxShadow: '0 12px 32px rgba(0,0,0,0.4)',
          padding: '4px 0', zIndex: 500
        }}>
          <button
            onClick={() => { onChange?.({ palette: [], name: '' }); setOpen(false); }}
            style={rowStyle(T, !themePalette.length)}
          >
            {!themePalette.length ? <Check size={12} /> : <span style={{ width: 12 }} />}
            <span style={{ flex: 1 }}>Default</span>
          </button>
          <div style={{ height: 1, background: T.border, margin: '4px 0' }} />
          {THEME_PRESETS.map(p => {
            const active = themeName === p.name;
            return (
              <button
                key={p.name}
                onClick={() => { onChange?.({ palette: p.palette, name: p.name }); setOpen(false); }}
                style={rowStyle(T, active)}
              >
                {active ? <Check size={12} color={T.accent} /> : <span style={{ width: 12 }} />}
                <span style={{ flex: 1 }}>{p.name}</span>
                <div style={{ display: 'inline-flex', gap: 2 }}>
                  {p.palette.slice(0, 6).map((c, i) => (
                    <div key={i} style={{
                      width: 10, height: 10, borderRadius: 2, background: c,
                      border: `1px solid ${T.border}`
                    }} />
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function rowStyle(T, active) {
  return {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '6px 12px', background: active ? T.hover : 'transparent',
    border: 'none', color: T.text, fontSize: 12, cursor: 'pointer',
    textAlign: 'left'
  };
}
